import React, {CSSProperties, useCallback, useContext, useEffect, useState} from "react";
import {MapController} from "../map/MapController";
import {Vector2} from "@utility/vector2";
import {update as updateMap} from "@redux/slices/map";
import {update as updateDialog} from "@redux/slices/dialog";
import {connect} from "react-redux";
import {selectMap} from "@redux/selectors/map";
import {GameContext} from "@components/game/GameProvider";
import {GameEvent} from "@game/gameEvent";
import {Update} from "@redux/slices/base";
import {Loading} from "@components/Loading";
import {Dialogs} from "@components/core/Dialogs";
import {Alerts} from "@components/core/Alerts";
import {MapBorder} from "@components/map/MapBorder";
import {AdSense} from "@components/interface/AdSense";
import {BannerAdvert} from "@components/interface/BannerAdvert";

const styles: Record<string, CSSProperties> = {
    advert: {
        backgroundColor: "white",
        backgroundSize: "cover",
        backgroundImage: "url(/images/backgrounds/advert.png)",
        margin: 0,
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        zIndex: 200
    },
    content: {
        backgroundColor: "black",
        touchAction: "none",
        position: "absolute",
        left: 0,
        right: 0,
        top: 0,
        bottom: 0
    }
};

interface IWordland {
    updateMap: Update
    updateDialog: Update
}

function Wordland({updateMap, updateDialog}: IWordland) {
    const [fade, setFade] = useState(0);
    const [focus, setFocus] = useState(Vector2.zero);
    const [, gameState] = useState<{}>();
    const game = useContext(GameContext);
    const forceUpdate = useCallback(() => gameState({}), []);
    const loadingStyle ={
        display: fade >= 1 ? "none" : "block",
        opacity: 1 - fade
    };
    const contentStyle = {
        ...styles.content,
        opacity: fade
    };

    useEffect(() => {
        const focusOnPlayer = () => {
            if (game.userPlayer) {
                const focus = game.userPlayer.getPosition(game.size);

                setFocus(focus);
                updateMap({
                    store: ["user"],
                    update: game.userPlayer.playerId
                });
            }
        };
        const delegate = (event: GameEvent) => {
            if (event.type === "UserPlayerInitialised") {
                focusOnPlayer();
                const pendingJoin = localStorage.getItem("pendingCompetitionJoin");
                if (pendingJoin) {
                    if (!game.userPlayer?.anonymous) {
                        updateDialog({update: {name: "competitionInvite", data: {code: pendingJoin}}});
                        localStorage.removeItem("pendingCompetitionJoin");
                    } else {
                        updateDialog({update: {name: "authenticate", data: {return: {name: "competitionInvite", data: {code: pendingJoin}}}}});
                    }
                } else if (!localStorage.getItem("hasSeenTutorial")) {
                    updateDialog({update: {name: "tutorial"}});
                }
            } else if (event.type === "PlayerMoved") {
                const position = game.userPlayer!.getPosition(game.size);

                updateMap({store: ["player"], update: position.serialize()});
            }
            forceUpdate();
        };

        game.events.delegates.add(delegate);
        game.userPlayer?.events.delegates.add(delegate);
        return () => {
            game.events.delegates.remove(delegate);
            game.userPlayer?.events.delegates.remove(delegate);
        }
    }, [forceUpdate, game, game.userPlayer]);
    useEffect(() => {
        if (game.initialised) {
            const interval = setInterval(() =>
                setFade(fade => fade + 0.006), 10);

            setTimeout(() => {
                clearInterval(interval);
                setFade(1);
            }, 2000);
        }
    }, [game.initialised]);
    return (
        <div>
            {game.initialised &&
                <div style={contentStyle}>
                    {game.userPlayer && <MapController focus={focus} game={game} userPlayer={game.userPlayer}/>}
                    <MapBorder/>
                    <Dialogs game={game} onNavigate={setFocus}/>
                    <Alerts game={game} ready={fade >= 1}/>
                </div>
            }
            <div style={loadingStyle}>
                <Loading game={game}/>
            </div>
        </div>
    );
}

const mapStateToProps = (store: never) => ({
    map: selectMap(store)
});
const connected = connect(mapStateToProps, {
    updateMap,
    updateDialog
})(Wordland);
export {connected as Wordland}
